<script setup lang="ts">
import { computed } from 'vue';
import ValidationInput from '@/components/ValidationInput.vue';
import type { ValidationInputCustomisation } from '@/components/ValidationInput.types';
import { MAX_GUEST_FIELDS_CHAR } from '@/common/config';
import { useI18n } from '@/plugins/i18n';
import {
  useGuestFormAllCustomisation,
  useGuestFieldRequirements,
} from '@/views/ChatWidget/composables/guest-form/useGuestFormCustomisation';
import type { GuestFieldErrors, GuestFormBoundModel, GuestTicketForm } from './openRequestForm.types';

/** Default `v-model` (Vue 2): prop `value`, event `input` — same contract as Chatroom `GuestFormContent`. */
const props = defineProps<{
  value: GuestFormBoundModel;
  fieldErrors: GuestFieldErrors;
  showDescription?: boolean;
  descriptionHint?: string;
  /** Force the email field to render as required (e.g. OTP flow needs an email) regardless of the customisation toggle. */
  emailForceRequired?: boolean;
}>();

const emit = defineEmits<{
  (e: 'clear-field-error', field: keyof GuestFieldErrors): void;
  (e: 'input', value: GuestFormBoundModel): void;
}>();

const emitFormPatch = (patch: Partial<GuestTicketForm>) => {
  emit('input', { ...props.value, ...patch });
};

const showDescriptionResolved = computed(() => props.showDescription !== false);

const { t } = useI18n();

const guestCustomisation = useGuestFormAllCustomisation();
const { nameInput, phoneInput, emailInput, descriptionInput, descriptionCharCounter } = guestCustomisation;
const { nameOptional, emailOptional, descriptionOptional, phoneOptional } = useGuestFieldRequirements();

const descriptionHintResolved = computed(() => props.descriptionHint ?? t('global.describe'));

const isDescriptionExceed = computed(() => props.value.description.length > MAX_GUEST_FIELDS_CHAR.description);

const withRequiredMark = (label: string, optional: boolean) => {
  const trimmed = label.trim();
  if (optional) {
    return trimmed.startsWith('*') ? trimmed.slice(1).trim() : trimmed;
  }
  return trimmed.startsWith('*') ? trimmed : `* ${trimmed}`;
};

const namePlaceholder = computed(() => {
  const custom = nameInput.placeholder.value?.trim();
  return withRequiredMark(custom || (t('global.name') as string), nameOptional.value);
});

const phonePlaceholder = computed(() => {
  const custom = phoneInput.placeholder.value?.trim();
  return withRequiredMark(custom || (t('global.phone') as string), phoneOptional.value);
});

const emailPlaceholder = computed(() => {
  const custom = emailInput.placeholder.value?.trim();
  return withRequiredMark(custom || (t('global.email') as string), emailOptional.value && !props.emailForceRequired);
});

const descriptionPlaceholder = computed(() => {
  const custom = descriptionInput.placeholder.value?.trim();
  return withRequiredMark(custom || descriptionHintResolved.value, descriptionOptional.value);
});

const nameCustomisation = computed((): ValidationInputCustomisation => {
  const hasError = Boolean(props.fieldErrors.name);
  return {
    backgroundColor: nameInput.backgroundColor.value ?? undefined,
    textColor: nameInput.textColor.value ?? undefined,
    placeholderColor: nameInput.placeholderColor.value ?? undefined,
    borderColor: hasError
      ? (nameInput.errorBorderColor.value ?? nameInput.borderColor.value ?? undefined)
      : (nameInput.borderColor.value ?? undefined),
    errorColor: nameInput.errorBorderColor.value ?? undefined,
  };
});

const phoneCustomisation = computed((): ValidationInputCustomisation => {
  const hasError = Boolean(props.fieldErrors.phone);
  return {
    backgroundColor: phoneInput.backgroundColor.value ?? undefined,
    textColor: phoneInput.textColor.value ?? undefined,
    placeholderColor: phoneInput.placeholderColor.value ?? undefined,
    borderColor: hasError
      ? (phoneInput.errorBorderColor.value ?? phoneInput.borderColor.value ?? undefined)
      : (phoneInput.borderColor.value ?? undefined),
    errorColor: phoneInput.errorBorderColor.value ?? undefined,
  };
});

const emailCustomisation = computed((): ValidationInputCustomisation => {
  const hasError = Boolean(props.fieldErrors.email);
  return {
    backgroundColor: emailInput.backgroundColor.value ?? undefined,
    textColor: emailInput.textColor.value ?? undefined,
    placeholderColor: emailInput.placeholderColor.value ?? undefined,
    borderColor: hasError
      ? (emailInput.errorBorderColor.value ?? emailInput.borderColor.value ?? undefined)
      : (emailInput.borderColor.value ?? undefined),
    errorColor: emailInput.errorBorderColor.value ?? undefined,
  };
});

const descriptionCustomisation = computed((): ValidationInputCustomisation => {
  const hasError = Boolean(props.fieldErrors.description);
  return {
    backgroundColor: descriptionInput.backgroundColor.value ?? undefined,
    textColor: descriptionInput.textColor.value ?? undefined,
    placeholderColor: descriptionInput.placeholderColor.value ?? undefined,
    borderColor: hasError
      ? (descriptionInput.errorBorderColor.value ?? descriptionInput.borderColor.value ?? undefined)
      : (descriptionInput.borderColor.value ?? undefined),
    errorColor: descriptionInput.errorBorderColor.value ?? undefined,
  };
});

const descriptionCounterStyle = computed(() => {
  const color = isDescriptionExceed.value
    ? (descriptionCharCounter.highlightColor.value ?? descriptionCharCounter.textColor.value)
    : descriptionCharCounter.textColor.value;
  return color ? { color } : {};
});

const handleNameInput = (value: string) => {
  emitFormPatch({ name: value });
};

const handlePhoneInput = (value: string) => {
  emitFormPatch({ phone: value });
};

const handleEmailInput = (value: string) => {
  emitFormPatch({ email: value });
};

const handleDescriptionInput = (value: string) => {
  emitFormPatch({ description: value });
};

const handleClear = (field: keyof GuestFieldErrors) => {
  emit('clear-field-error', field);
};
</script>

<template>
  <div class="open-request-guest-fields theme-font-main">
    <div class="open-request-guest-fields__field" data-testid="guest-field-name" @focusin="handleClear('name')">
      <ValidationInput
        :value="value.name"
        type="text"
        :placeholder="namePlaceholder"
        :show-error="Boolean(fieldErrors.name)"
        :error-message="fieldErrors.name"
        :customisation="nameCustomisation"
        :max-length="MAX_GUEST_FIELDS_CHAR.name"
        class="open-request-guest-fields__input"
        @input="handleNameInput"
      />
    </div>

    <div class="open-request-guest-fields__field" data-testid="guest-field-phone" @focusin="handleClear('phone')">
      <ValidationInput
        :value="value.phone"
        type="tel"
        :placeholder="phonePlaceholder"
        :show-error="Boolean(fieldErrors.phone)"
        :error-message="fieldErrors.phone"
        :customisation="phoneCustomisation"
        :max-length="MAX_GUEST_FIELDS_CHAR.phone"
        class="open-request-guest-fields__input"
        @input="handlePhoneInput"
      />
    </div>

    <div class="open-request-guest-fields__field" data-testid="guest-field-email" @focusin="handleClear('email')">
      <ValidationInput
        :value="value.email"
        type="email"
        :placeholder="emailPlaceholder"
        :show-error="Boolean(fieldErrors.email)"
        :error-message="fieldErrors.email"
        :customisation="emailCustomisation"
        :max-length="MAX_GUEST_FIELDS_CHAR.email"
        class="open-request-guest-fields__input"
        @input="handleEmailInput"
      />
    </div>

    <div
      v-if="showDescriptionResolved"
      class="open-request-guest-fields__field"
      data-testid="guest-field-description"
      @focusin="handleClear('description')"
    >
      <ValidationInput
        tag="textarea"
        :value="value.description"
        :placeholder="descriptionPlaceholder"
        :show-error="Boolean(fieldErrors.description)"
        :error-message="fieldErrors.description"
        :customisation="descriptionCustomisation"
        :max-length="MAX_GUEST_FIELDS_CHAR.description"
        class="open-request-guest-fields__input"
        @input="handleDescriptionInput"
      />
      <p class="open-request-guest-fields__counter theme-font-subtext" :style="descriptionCounterStyle">
        {{ value.description.length }} / {{ MAX_GUEST_FIELDS_CHAR.description }}
      </p>
    </div>
  </div>
</template>

<style scoped lang="scss">
.open-request-guest-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
}

.open-request-guest-fields__field {
  width: 100%;
}

.open-request-guest-fields__input {
  width: 100%;
}

.open-request-guest-fields__counter {
  text-align: right;
  margin-top: 4px;
}
</style>
